import { Link, useParams } from 'react-router-dom';
import { Alert, Col, Typography } from 'antd';
import { FolderFilled } from '@ant-design/icons';
import { Media, MediaType, useProductQuery } from '../apollo/api';
import BackButton from '../ui-components/BackButton';
import Bookcase from '../ui-components/Bookcase';
import BookcaseSection from '../ui-components/BookcaseSection';
import MediaIcon from '../ui-components/MediaIcon';

export default function Product() {
  const { productId, path = '' } = useParams<{ productId: string; path?: string }>();

  const { loading, error, data } = useProductQuery({ variables: { id: productId } });

  const productInstance = data?.productInstances[0];
  const { product } = productInstance ?? {};

  const mediaPerType = product?.media.reduce(
    (
      media: {
        [MediaType.PDF]?: Media[];
        [MediaType.EPUB]?: Media[];
        [MediaType.AUDIO]?: Media[];
        [MediaType.VIDEO]?: Media[];
        dirs?: {
          [MediaType.PDF]?: string[];
          [MediaType.EPUB]?: string[];
          [MediaType.AUDIO]?: string[];
          [MediaType.VIDEO]?: string[];
          misc?: string[];
        };
      },
      m
    ) => {
      if ((m.path ?? '') === path) {
        return { ...media, [m.type]: [...(media[m.type] ?? []), m] };
      } else if (m.path?.startsWith(path) && (path.length < 1 || m.path[path.length] === '/')) {
        const dir = (path.length > 0 ? m.path.slice(path.length + 1) : m.path).split('/')[0]!;
        const dirs = media.dirs ?? {};
        if (dirs.misc?.includes(dir) || dirs[m.type]?.includes(dir)) {
          return media;
        }
        const otherType = Object.values(MediaType).find((t) => t !== m.type && dirs[t]?.includes(dir));
        if (otherType) {
          return {
            ...media,
            dirs: {
              ...dirs,
              [otherType]: dirs[otherType]!.length > 1 ? dirs[otherType]!.filter((d: string) => d !== dir) : undefined,
              misc: [...(dirs.misc ?? []), dir],
            },
          };
        } else {
          return {
            ...media,
            dirs: {
              ...dirs,
              [m.type]: [...(dirs[m.type] ?? []), dir],
            },
          };
        }
      } else {
        return media;
      }
    },
    {}
  );

  function sortDirs(dirs?: string[]) {
    return dirs?.sort((a, b) => a.localeCompare(b));
  }
  function sortMedia(media?: Media[]) {
    return media?.sort((a, b) => a.key.localeCompare(b.key));
  }

  return (
    <>
      {loading && <p>Caricamento...</p>}

      {error && <Alert message={error.message} type="error" showIcon />}

      {product && (
        <>
          <div className="Header">
            <BackButton className="Action-back" />
            <h1>
              <span dangerouslySetInnerHTML={{ __html: product?.description ?? '' }} />
              {path.length > 0 && <span>{' / ' + path.split('/').join(' / ')}</span>}
            </h1>
          </div>

          <Bookcase>
            {[MediaType.PDF, MediaType.EPUB, MediaType.AUDIO, MediaType.VIDEO].map((type) => {
              const dirs = sortDirs(mediaPerType?.dirs?.[type]);
              const media = sortMedia(mediaPerType?.[type]);
              return (
                (dirs || media) && (
                  <Section type={type} dirs={dirs} media={media} allMedia={product.media} path={path} key={type} />
                )
              );
            })}
            {mediaPerType?.dirs?.misc && <Section dirs={sortDirs(mediaPerType?.dirs?.misc)!} />}
          </Bookcase>
        </>
      )}
    </>
  );
}

function Section(
  p: { type: MediaType; dirs?: string[]; media?: Media[]; allMedia: Media[]; path: string } | { dirs: string[] }
) {
  const isMisc = !('type' in p);
  return (
    <BookcaseSection title={isMisc ? 'CONTENUTI' : p.type}>
      {p.dirs?.map((d) => (
        <Link to={(location) => `${location.pathname}/${d}`} key={d}>
          <Col className="Library-media-item">
            <FolderFilled className="Directory-icon" />
            <Typography.Paragraph ellipsis={{ rows: 3 }} className="Library-item-title">
              <span dangerouslySetInnerHTML={{ __html: d }} />
            </Typography.Paragraph>
          </Col>
        </Link>
      ))}
      {!isMisc &&
        p.media?.map((m) => (
          <Link
            to={(location) =>
              `${
                p.path.length > 0
                  ? location.pathname.slice(0, location.pathname.length - p.path.length - 1)
                  : location.pathname
              }/${p.allMedia.indexOf(m)}`
            }
            key={m.key}
          >
            <Col className="Library-media-item">
              <MediaIcon type={p.type} />
              <Typography.Paragraph ellipsis={{ rows: 3 }} className="Library-item-title">
                <span dangerouslySetInnerHTML={{ __html: m.description }} />
              </Typography.Paragraph>
            </Col>
          </Link>
        ))}
    </BookcaseSection>
  );
}
